'use client';

import { useEffect } from 'react';

interface Props {
  productName: string;
  description?: string;
  thumbnailUrl?: string;
  productUrl?: string;
  metadata?: Record<string, any>;
}

export function SocialMeta({ productName, description, thumbnailUrl, productUrl, metadata }: Props) {
  useEffect(() => {
    const social = (metadata as any)?.socialSharing || {};
    const title = social.title || `${productName} | 3D Configurator`;
    const desc = social.description || description || `Customize ${productName} in 3D`;
    const image = social.image || thumbnailUrl;
    const url = social.url || productUrl || window.location.href;

    setMeta('og:title', title);
    setMeta('og:description', desc);
    setMeta('og:type', 'product');
    setMeta('og:url', url);
    if (image) setMeta('og:image', image);

    setMeta('twitter:card', image ? 'summary_large_image' : 'summary');
    setMeta('twitter:title', title);
    setMeta('twitter:description', desc);
    if (image) setMeta('twitter:image', image);

    document.title = title;

    return () => {
      removeMeta('og:title');
      removeMeta('og:description');
      removeMeta('og:type');
      removeMeta('og:url');
      removeMeta('og:image');
      removeMeta('twitter:card');
      removeMeta('twitter:title');
      removeMeta('twitter:description');
      removeMeta('twitter:image');
    };
  }, [productName, description, thumbnailUrl, productUrl, metadata]);

  return null;
}

function setMeta(property: string, content: string) {
  const isOg = property.startsWith('og:') || property.startsWith('twitter:');
  const attr = isOg ? 'property' : 'name';
  let tag = document.querySelector(`meta[${attr}="${property}"]`) as HTMLMetaElement | null;
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, property);
    document.head.appendChild(tag);
  }
  tag.content = content;
}

function removeMeta(property: string) {
  const isOg = property.startsWith('og:') || property.startsWith('twitter:');
  const attr = isOg ? 'property' : 'name';
  const tag = document.querySelector(`meta[${attr}="${property}"]`);
  if (tag) tag.remove();
}
